import TextField from '@mui/material/TextField'
import React, { useEffect, useState } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import InputLabel from '@mui/material/InputLabel'
import FormControl from '@mui/material/FormControl'
import CircularProgress from '@mui/material/CircularProgress'
import Autocomplete from '@mui/material/Autocomplete'
import { getEntityTypes, SaveEntity, globalAdminGenerateQr } from '@api'
import { ROUND_EQUIPMENT_TYPE, TAG_TYPE } from '@constant/qrcode'
import SnackBar from '@component/SnackBar'

interface Props {
  open: boolean
  onClose: () => void
  groupId: string
  buildingId: string
}

const STOP_TYPE_OPTIONS = [
  { label: 'Equipment', value: 'Equipment' },
  { label: 'Location', value: 'Location' },
]

const NewEquipmentDialog: React.FC<Props> = ({ open, onClose, groupId, buildingId }) => {
  const [name, setName] = useState('')
  const [tagType, setTagType] = useState('QROnly')
  const [stopType, setStopType] = useState('Equipment')
  const [equipmentType, setEquipmentType] = useState<any>(null)
  const [floor, setFloor] = useState('')
  const [entityType, setEntityType] = useState('')
  const [mechanicalSpace, setMechanicalSpace] = useState('')
  const [installationNote, setInstallationNote] = useState('')
  const [entityTypes, setEntityTypes] = useState<any[]>([])
  const [saving, setSaving] = useState(false)

  const isBuilding = entityType === 'Building'
  const equipmentTypeOptions = stopType === 'Equipment' ? ROUND_EQUIPMENT_TYPE : entityTypes

  const canSubmit =
    !!name &&
    !!tagType &&
    !!stopType &&
    !!equipmentType &&
    !!entityType &&
    !!mechanicalSpace &&
    (isBuilding || (!!floor && !!installationNote))

  useEffect(() => {
    if (!open) return
    getEntityTypes({})
      .then((data: any) => {
        setEntityTypes(
          (data || []).sort((a: any, b: any) => (a?.DisplayText?.en || '').localeCompare(b?.DisplayText?.en || ''))
        )
      })
      .catch(() => {})
  }, [open])

  const resetForm = () => {
    setName('')
    setTagType('QROnly')
    setStopType('Equipment')
    setEquipmentType(null)
    setFloor('')
    setEntityType('')
    setMechanicalSpace('')
    setInstallationNote('')
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const handleStopTypeChange = (value: string) => {
    setStopType(value)
    setEquipmentType(null)
  }

  const handleEntityTypeChange = (value: string) => {
    setEntityType(value)
    if (value === 'Building') {
      setFloor('')
      setInstallationNote('')
    }
    // when stopType is Location, equipment type mirrors the location type
    if (stopType === 'Location') {
      const match = entityTypes.find((t) => t.Name === value)
      setEquipmentType(match || null)
    }
  }

  const handleSubmit = async () => {
    setSaving(true)
    try {
      let entityId = buildingId

      if (!isBuilding) {
        const entityResult: any = await SaveEntity({
          GroupId: groupId,
          BuildingId: buildingId,
          Type: entityType,
          Floor: floor,
          Unit: '',
          NameEn: installationNote,
          Location: entityType,
          EntityId: '',
        })
        entityId = entityResult?.acId
      }

      await globalAdminGenerateQr({
        GroupId: groupId,
        EntityId: entityId,
        Floor: floor,
        Unit: '',
        Name: name,
        QrType: 'EquipmentInspection',
        QrStyle: 'Activate',
        TagType: tagType,
        MechanicalSpace: mechanicalSpace,
        EquipmentType: equipmentType?.Name || equipmentType,
        StopType: stopType,
      })

      SnackBar({ msg: 'Equipment and QR created successfully!', type: 'success' })
      handleClose()
    } catch (e: any) {
      SnackBar({ msg: e?.message || 'Failed to create equipment', type: 'error' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>New Equipment</DialogTitle>
      <DialogContent>
        <TextField
          label="Equipment Name"
          required
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
          variant="outlined"
          style={{ marginTop: 16, marginBottom: 12 }}
        />

        <FormControl fullWidth style={{ marginBottom: 12 }}>
          <InputLabel>Tag Type</InputLabel>
          <Select value={tagType} onChange={(e) => setTagType(e.target.value as string)} label="Tag Type">
            {TAG_TYPE.map((t) => (
              <MenuItem key={t.value} value={t.value}>
                {t.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth style={{ marginBottom: 12 }}>
          <InputLabel>Stop Type</InputLabel>
          <Select value={stopType} onChange={(e) => handleStopTypeChange(e.target.value as string)} label="Stop Type">
            {STOP_TYPE_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Autocomplete
          value={equipmentType}
          onChange={(_, value) => setEquipmentType(value)}
          options={equipmentTypeOptions}
          getOptionLabel={(option) => option?.DisplayText?.en || ''}
          isOptionEqualToValue={(option, value) => option?.Name === value?.Name}
          style={{ marginBottom: 12 }}
          renderInput={(params) => <TextField {...params} label="Equipment Type" variant="outlined" required />}
        />

        <FormControl fullWidth required style={{ marginBottom: 12 }}>
          <InputLabel>Location Type</InputLabel>
          <Select
            value={entityType}
            onChange={(e) => handleEntityTypeChange(e.target.value as string)}
            label="Location Type"
          >
            {entityTypes.map((t) => (
              <MenuItem key={t.Name} value={t.Name}>
                {t.DisplayText?.en}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {!isBuilding && (
          <>
            <TextField
              label="Floor"
              required
              fullWidth
              value={floor}
              onChange={(e) => setFloor(e.target.value)}
              variant="outlined"
              style={{ marginBottom: 12 }}
            />
            <TextField
              label="Installation Note"
              required
              fullWidth
              value={installationNote}
              onChange={(e) => setInstallationNote(e.target.value)}
              variant="outlined"
              style={{ marginBottom: 12 }}
            />
          </>
        )}

        <TextField
          label="Mechanical Space"
          required
          fullWidth
          value={mechanicalSpace}
          onChange={(e) => setMechanicalSpace(e.target.value)}
          variant="outlined"
          style={{ marginBottom: 12 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={!canSubmit || saving} variant="contained" color="primary">
          {saving ? <CircularProgress size={20} color="inherit" /> : 'Generate QR'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default NewEquipmentDialog
